import { DataManager } from '@syncfusion/ej2-data';
import { Component, Input, OnInit, ViewChild, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FileInfo } from '@syncfusion/ej2-angular-inputs';
import { DropDownListComponent, DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { SwitchComponent } from '@syncfusion/ej2-angular-buttons';
import { DropDownConstantsFactory } from 'src/app/base/shared/syncfusion/dropdown-constants-factory';
import { AssetStorageEntryLicense, AssetStorageKeyWordDTO, AssetType, ImageQuality, PagedResultDTOOfAssetStorageKeyWordDTO } from 'src/app/base/models/models.swagger.generated';
import { DropDownItemTyped } from 'src/app/base/shared/drop-down-item.model';
import { ToastService } from 'src/app/base/services/toast.service';
import { EnumTranslationService } from 'src/app/base/services/enum-translation.service';
import { AssetConfigurationsService } from '../../../../asset-configurations.service';
import { createElement } from '@syncfusion/ej2-base';
import { AssetStorageUploadEditorFormType } from './asset-storage-upload-editor-form-type';
import { AssetKeyWordService } from '../asset-key-word-service/asset-key-word.service';
import { MultiSelectCustomAdaptor } from 'src/app/base/shared/multi-select-filter/multi-select-custom-adaptor';
import { Observable, map } from 'rxjs';
import { MultiSelectItem } from 'src/app/base/shared/autocomplete/multi-select-item';
import { MultiSelectFilterType } from 'src/app/base/shared/multi-select-filter/multi-select-filter-type';
import { MultiSelectFilterComponent } from 'src/app/base/shared/multi-select-filter/multi-select-filter.component';
import { TranslateModule } from '@ngx-translate/core';
import { MultiSelectFilterComponent as MultiSelectFilterComponent_1 } from '../../../../../../base/shared/multi-select-filter/multi-select-filter.component';
import { AppErrorMessagesComponent } from '../../../../../../base/error-messages/app-error-messages.component';
import { CommonModule } from '@angular/common';
import { YesNoGroupButtonComponent } from 'src/app/base/shared/yes-no-group-button/yes-no-group-button.component';
import { AssetStorageAddKeyWordDialogComponent } from '../asset-storage-add-key-word-dialog/asset-storage-add-key-word-dialog.component';

@Component({
    selector: 'app-asset-storage-edit-detail-form',
    templateUrl: './asset-storage-edit-detail-form.component.html',
    styleUrls: ['./asset-storage-edit-detail-form.component.scss'],
    imports: [ReactiveFormsModule, DropDownListModule, AppErrorMessagesComponent, MultiSelectFilterComponent_1, AssetStorageAddKeyWordDialogComponent, TranslateModule, YesNoGroupButtonComponent, CommonModule]
})
export class AssetStorageEditDetailFormComponent implements OnInit, AfterViewChecked {

  @Input()
  public assetStorageUploadEditorForm: FormGroup<AssetStorageUploadEditorFormType>;

  @Input()
  public isBrandLogoMode: boolean = false;

  @ViewChild('typeDropdown')
  public typeDropdown: DropDownListComponent;

  @ViewChild("replaceCustomizedLogos")
  public replaceCustomizedLogos: SwitchComponent;

  @ViewChild("addKeyWordDialog")
  public addKeyWordDialog: AssetStorageAddKeyWordDialogComponent;

  @ViewChild("multiselectFilter")
  public multiselectFilter: MultiSelectFilterComponent;

  public parentElement: HTMLElement;

  public languages: string[] = [];

  public imageQualities = this.enumTranslateService.translateNumericEnum(ImageQuality, 'i18n.shared.ImageQualities.');

  public enumDropdownSettings = DropDownConstantsFactory.createNumericEnumDropdownSettings();

  public licenses = this.enumTranslateService.translateNumericEnum(AssetStorageEntryLicense, 'i18n.shared.AssetStorageEntryLicenses.');
  public fields = { text: 'text', value: 'value' };

  public get selectedKeyWordId(): number[] {
    if (this.assetStorageUploadEditorForm.controls.assignedAssetKeyWords.value === null) {
      return null;
    }
    return this.assetStorageUploadEditorForm.controls.assignedAssetKeyWords.value;
  }
  public set selectedKeyWordId(value: number[]) {
    this.assetStorageUploadEditorForm.controls.assignedAssetKeyWords.setValue(value);
  }

  public assetKeyWordsData: DataManager;
  public MultiSelectFilterType = MultiSelectFilterType;

  public assetTypes: DropDownItemTyped<typeof AssetType>[] = [];
  private _allAvailableAssetTypes = this.enumTranslateService.translateNumericEnum(AssetType, 'i18n.shared.assetTypes.').filter((type) => (type.value as any) !== (AssetType.Unknown as any));

  constructor(
    public toastService: ToastService,
    private enumTranslateService: EnumTranslationService,
    private assetConfigurationsService: AssetConfigurationsService,
    private cdr: ChangeDetectorRef,
    private assetKeyWordService: AssetKeyWordService
  ) { }

  ngOnInit(): void {
    this.assetTypes = this._allAvailableAssetTypes;
    const config = this.assetConfigurationsService.assetConfiguration;
    if (config) {
      this.languages = config.supportedAssetLanguages;
    }

    this.assetKeyWordsData = new DataManager({
      adaptor: new MultiSelectCustomAdaptor(this.searchAssetStorageKeyWords.bind(this)),
    });

    this.toggleOriginalFilenameField();
    this.assetStorageUploadEditorForm.controls.isStaticFileName.valueChanges.subscribe(() => {
      this.toggleOriginalFilenameField();
    });
  }

  public ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  public get isImage(): boolean {
    // check if the selected file extetnsion is an image
    return this.assetStorageUploadEditorForm.controls.fileExtension.value?.toLowerCase()?.match(/(jpg|jpeg|png|gif)$/i) != null;
  }

  public resetMultiselectFilter() {
    this.multiselectFilter.refreshData();
  }

  public formSelectedData(file: FileInfo, proxy: any): void {
    let liEle: HTMLElement = createElement('li', { className: 'e-upload-file-list', attrs: { 'data-file-name': file.name } });
    let imageTag: HTMLImageElement = <HTMLImageElement>createElement('IMG', { className: 'upload-image', attrs: { 'alt': 'Image' } });
    let wrapper: HTMLElement = createElement('span', { className: 'wrapper' });
    wrapper.appendChild(imageTag);
    liEle.appendChild(wrapper);
    this.readURL(liEle, file);
    document.querySelector('.e-upload-files').appendChild(liEle);
  }

  public readURL(li: HTMLElement, args: any): void {
    let preview: HTMLImageElement = li.querySelector('.upload-image');
    let file: File = args.rawFile;
    let reader: FileReader = new FileReader();
    reader.addEventListener('load', () => { preview.src = reader.result as string; }, false);
    if (file) {
      reader.readAsDataURL(file);
    }
  }

  public canSave(): boolean {
    return this.assetStorageUploadEditorForm?.valid;
  }

  public onAddKeyWord() {
    this.addKeyWordDialog.showDialog();
  }

  public onKeyWordAdded(keyWord: AssetStorageKeyWordDTO) {
    this.assetStorageUploadEditorForm.controls.assignedAssetKeyWords.setValue([...this.assetStorageUploadEditorForm.controls.assignedAssetKeyWords.value, keyWord.id]);
    this.multiselectFilter.refreshData();
  }

  public searchAssetStorageKeyWords(searchText: string): Observable<MultiSelectItem[]> {
    return this.assetKeyWordService.searchKeyWords(searchText)
      .pipe(map((keyWords: PagedResultDTOOfAssetStorageKeyWordDTO) => {
        const data: MultiSelectItem[] = [];
        keyWords.items.forEach(kw => {
          data.push({ id: kw.id, name: kw.name });
        });
        return data;
      }));
  }

  private toggleOriginalFilenameField() {
    if (this.assetStorageUploadEditorForm.controls.isStaticFileName.value && !this.assetStorageUploadEditorForm.controls.id.value) {
      this.assetStorageUploadEditorForm.controls.originalFileName.enable();
      this.assetStorageUploadEditorForm.controls.originalFileName.setValidators([Validators.required]);
    } else {
      this.assetStorageUploadEditorForm.controls.originalFileName.disable();
      this.assetStorageUploadEditorForm.controls.originalFileName.clearValidators();
    }
  }
}
